/**
 * DAW Utilities
 * Pure utility functions for audio processing and formatting
 */

/**
 * Format time in seconds to MM:SS.mmm format
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

/**
 * Format time for simple display (MM:SS)
 */
export const formatTimeSimple = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Generate waveform data from AudioBuffer (average method)
 */
export const generateWaveform = (audioBuffer, numSamples = 200) => {
  if (!audioBuffer) return [];
  
  const channelData = audioBuffer.getChannelData(0);
  const blockSize = Math.floor(channelData.length / numSamples);
  const waveform = [];
  
  for (let i = 0; i < numSamples; i++) {
    const start = i * blockSize;
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(channelData[start + j] || 0);
    }
    waveform.push(sum / blockSize);
  }
  
  // Normalize
  const max = Math.max(...waveform);
  return waveform.map(v => v / (max || 1));
};

/**
 * Generate waveform data from AudioBuffer (peak method)
 */
export const generateWaveformPeaks = (buffer, samples = 200) => {
  if (!buffer) return [];
  
  const channelData = buffer.getChannelData(0);
  const blockSize = Math.floor(channelData.length / samples);
  const peaks = [];
  
  for (let i = 0; i < samples; i++) {
    const start = blockSize * i;
    let max = 0;
    
    for (let j = 0; j < blockSize; j++) {
      const value = Math.abs(channelData[start + j] || 0);
      if (value > max) max = value;
    }
    
    peaks.push(max);
  }
  
  return peaks;
};

/**
 * Convert AudioBuffer to WAV format
 */
export const audioBufferToWav = (audioBuffer) => {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  let data;
  if (numChannels === 2) {
    const left = audioBuffer.getChannelData(0);
    const right = audioBuffer.getChannelData(1);
    data = new Float32Array(left.length + right.length);
    for (let i = 0; i < left.length; i++) {
      data[i * 2] = left[i];
      data[i * 2 + 1] = right[i];
    }
  } else {
    data = audioBuffer.getChannelData(0);
  }

  const buffer = new ArrayBuffer(44 + data.length * 2);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + data.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bitDepth / 8, true);
  view.setUint16(32, numChannels * bitDepth / 8, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, data.length * 2, true);

  // Write audio data
  let offset = 44;
  for (let i = 0; i < data.length; i++) {
    const sample = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
    offset += 2;
  }

  return buffer;
};

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

/**
 * Convert AudioBuffer to base64
 */
export const audioBufferToBase64 = async (audioBuffer) => {
  const wavData = audioBufferToWav(audioBuffer);
  const bytes = new Uint8Array(wavData);
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
};

/**
 * Convert base64 to AudioBuffer
 */
export const base64ToAudioBuffer = async (base64, audioContext) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const audioBuffer = await audioContext.decodeAudioData(bytes.buffer);
  return audioBuffer;
};

/**
 * Calculate total duration from tracks
 */
export const calculateTotalDuration = (tracks, defaultDuration = 60) => {
  if (!tracks || tracks.length === 0) return defaultDuration;
  
  const maxTrackDuration = tracks.reduce((max, track) => {
    const trackEnd = (track.startTime || 0) + (track.duration || 0);
    return Math.max(max, trackEnd);
  }, 0);
  
  return Math.max(defaultDuration, maxTrackDuration + 10); // Add 10s buffer
};

/**
 * MIDI note number to note name
 */
export const midiNoteToName = (noteNumber) => {
  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(noteNumber / 12) - 1;
  const noteName = notes[noteNumber % 12];
  return `${noteName}${octave}`;
};

/**
 * Note name to MIDI note number
 */
export const noteNameToMidi = (noteName) => {
  const match = noteName.match(/^([A-G]#?)(-?\d+)$/i);
  if (!match) return null;
  
  const notes = { C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11 };
  const note = match[1].toUpperCase();
  const octave = parseInt(match[2]);
  
  return notes[note] + (octave + 1) * 12;
};

/**
 * BPM to seconds per beat
 */
export const bpmToSecondsPerBeat = (bpm) => 60 / bpm;

/**
 * Seconds to beats
 */
export const secondsToBeats = (seconds, bpm) => seconds * bpm / 60;

/**
 * Beats to seconds
 */
export const beatsToSeconds = (beats, bpm) => beats * 60 / bpm;

/**
 * Snap time to grid
 */
export const snapToGrid = (time, bpm, timeSignature = 4, subdivisions = 4) => {
  const beatsPerBar = timeSignature;
  const secondsPerBeat = 60 / bpm;
  const gridSize = secondsPerBeat / subdivisions;
  return Math.round(time / gridSize) * gridSize;
};

/**
 * Clamp value between min and max
 */
export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Linear interpolation
 */
export const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Convert decibels to linear gain
 */
export const dbToGain = (db) => Math.pow(10, db / 20);

/**
 * Convert linear gain to decibels
 */
export const gainToDb = (gain) => 20 * Math.log10(gain);

/**
 * Track colors
 */
export const TRACK_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
];

/**
 * Default BPM
 */
export const DEFAULT_BPM = 120;

/**
 * Max tracks
 */
export const MAX_TRACKS = 32;

/**
 * Max undo history depth
 */
export const MAX_HISTORY_DEPTH = 100;
